// Instagram path of the broadcast flow (channel = "instagram" in
// create-campaign/index.tsx). Uses the same route as every other broadcast;
// only the steps differ. Wired to the real Instagram ads API (api.ts) —
// senders, location/interest/behavior/locale search, custom audiences, reach
// estimate, and the campaign create/autosave/validate/launch lifecycle all
// hit real endpoints now (see instagram_ads.postman_collection.json).
// `conversionLocation`/`attribution`/`destinationDomain` have no slot in the
// documented payload and stay UI-only; `utm` is folded into the creative's
// destination_url as a query string at payload-build time (api.ts).
// `savedSegments` is intentionally never sent — the docs say segments aren't
// synced to Meta yet.

export type BuyingType = "auction" | "reach";
export type AudienceType = "advantage" | "manual";
export type Gender = "all" | "men" | "women";
export type CreativeFormat = "single" | "carousel" | "reel";
export type PlacementMode = "optimised" | "manual";
export type BudgetType = "Daily Budget" | "Lifetime Budget";

export interface LocationOption {
  key: string;
  type: "country" | "region" | "city";
  name: string;
}

export interface TargetingOption {
  id: string;
  name: string;
}

export interface LocaleOption {
  key: number;
  name: string;
}

export interface CustomAudienceOption {
  id: string;
  name: string;
}

export interface AdSet {
  id: string;
  name: string;
  audienceType: AudienceType;
  locations: LocationOption[];
  ageFrom: number;
  ageTo: number;
  gender: Gender;
  language: LocaleOption | null;
  savedSegments: string[];
  customAudiences: CustomAudienceOption[];
  interests: TargetingOption[];
  behaviours: TargetingOption[];
  placementMode: PlacementMode;
  manualPlacements: string[];
  optimization: string;
  conversionLocation: string;
  attribution: string;
  destinationDomain: string;
  utm: string;
  // Only required (and only sent) when the broadcast goal is Sales/Leads —
  // Meta rejects those without optimization.promoted_object (confirmed live:
  // "A 'sales' broadcast needs optimization.promoted_object on ad set 1").
  promotedObjectPixelId: string;
  promotedObjectEventType: string;
}

export interface BudgetState {
  budgetType: BudgetType;
  amount: number;
  startAt: string; // datetime-local value
  timezone: string;
  hasEnd: boolean;
  endAt: string;
}

export interface CreativeMedia {
  url: string;
  kind: "image" | "video";
  name: string;
}

export interface CreativeState {
  templateId: string | null;
  format: CreativeFormat;
  media: CreativeMedia | null;
  primaryText: string;
  headline: string;
  cta: string;
  destinationUrl: string;
}

export interface InstagramAdState {
  // The real sender id sent to the API; senderHandle is only the cached
  // display label ("@username") so step-review/the phone preview don't need
  // the whole senders list threaded through just to look one name up.
  senderId: string;
  senderHandle: string;
  goal: string;
  name: string;
  buyingType: BuyingType;
  specialCategory: string;
  adSets: AdSet[];
  creative: CreativeState;
  budget: BudgetState;
}

export const GOALS = ["Sales", "Traffic", "Leads", "Awareness", "Engagement"];
export const SPECIAL_CATEGORIES = ["Credit", "Employment", "Housing", "Social issues, elections or politics"];
export const AGES = Array.from({ length: 48 }, (_, i) => 18 + i); // 18–65
// Saved Segments aren't synced to Meta yet (per the docs) — kept as a
// client-only list rather than a live search, since there's nothing to
// search against and the selection is never sent.
export const SEGMENT_OPTIONS = ["High-value customers", "Cart abandoners", "Lapsed 90 days", "Newsletter subscribers"];
export const PLACEMENT_OPTIONS = ["Insta Feed", "Stories", "Reels", "Explore Feed"];
export const OPTIMIZATION_OPTIONS = ["Conversions", "Link clicks", "Landing page views", "Reach"];
export const CONVERSION_LOCATIONS = ["Website", "Instagram profile", "Messages", "Calls"];
export const ATTRIBUTION_OPTIONS = ["7-day click/ 1-day view", "1-day click", "7-day click", "1-day click/ 1-day view"];
export const TIMEZONES = ["Asia/ Kolkata UTC +05:30", "Africa/ Lagos UTC +01:00", "Europe/ London UTC +00:00", "America/ New_York UTC -05:00"];

// Location/Interests/Behaviours/Language/Custom-Audience pickers are meant
// to be scoped to the chosen sender's real Meta data (see api.ts's search
// hooks) — but until sender profiles are actually coming back from
// GET /senders/instagram, there's no senderId to scope them by, so those
// fields would otherwise sit empty/blocked. These are a temporary,
// hardcoded stand-in so the ad set can still be filled in end-to-end; once
// sender data is reliably flowing, ad-set-details.tsx goes back to the real
// per-sender search for everyone, not just users who happen to type a query.
export const FALLBACK_LOCATIONS: LocationOption[] = [
  { key: "IN", type: "country", name: "India" },
  { key: "NG", type: "country", name: "Nigeria" },
  { key: "GH", type: "country", name: "Ghana" },
  { key: "US", type: "country", name: "United States" },
];
export const FALLBACK_INTERESTS: TargetingOption[] = [
  { id: "interest_online_banking", name: "Online banking" },
  { id: "interest_mobile_payments", name: "Mobile payments" },
  { id: "interest_personal_finance", name: "Personal finance" },
  { id: "interest_small_business", name: "Small business" },
  { id: "interest_online_shopping", name: "Online shopping" },
];
export const FALLBACK_BEHAVIOURS: TargetingOption[] = [
  { id: "behavior_frequent_travelers", name: "Frequent travelers" },
  { id: "behavior_engaged_shoppers", name: "Engaged shoppers" },
  { id: "behavior_small_business_owners", name: "Small business owners" },
  { id: "behavior_mobile_device_users", name: "Mobile device users" },
];
export const FALLBACK_LOCALES: LocaleOption[] = [
  { key: 6, name: "English (UK)" },
  { key: 1, name: "English (US)" },
  { key: 40, name: "Hausa" },
  { key: 42, name: "Yoruba" },
  { key: 43, name: "Igbo" },
  { key: 33, name: "French (France)" },
];
export const FALLBACK_CUSTOM_AUDIENCES: CustomAudienceOption[] = [
  { id: "ca_high_value", name: "High-value customers" },
  { id: "ca_recent_purchasers", name: "Recent purchasers" },
  { id: "ca_cart_abandoners", name: "Cart abandoners" },
  { id: "ca_app_installers_90d", name: "App installers (90 days)" },
];

export const FORMAT_LABEL: Record<CreativeFormat, string> = {
  single: "Single Image",
  carousel: "Carousel",
  reel: "Reel/ Video",
};

// What the (static) ConvoAI panel says it pre-filled from.
export const DEFAULT_BRIEF = "A summer sale announcement for high-end sunglasses with a 20% discount code: SUMMER20";

let adSetCounter = 0;
// `defaultLocation` comes from the signed-in user's own org country
// (api.ts's useDefaultAdLocation) — India is only the fallback used before
// that resolves or if it fails.
export const createAdSet = (name = "", defaultLocation?: LocationOption): AdSet => ({
  id: `adset_${Date.now()}_${adSetCounter++}`,
  name,
  audienceType: "advantage",
  locations: [defaultLocation || { key: "IN", type: "country", name: "India" }],
  ageFrom: 25,
  ageTo: 45,
  gender: "all",
  // Placeholder locale id, matching the docs' own reach-estimate example —
  // replaced as soon as the user searches/picks a real one.
  language: { key: 6, name: "English (UK)" },
  savedSegments: [],
  customAudiences: [],
  interests: [],
  behaviours: [],
  placementMode: "optimised",
  manualPlacements: ["Insta Feed", "Stories"],
  optimization: "Conversions",
  conversionLocation: "Website",
  attribution: "7-day click/ 1-day view",
  destinationDomain: "vouchlinks.com",
  utm: "utm_source=instagram&utm_campaign=sept_product_c..",
  promotedObjectPixelId: "",
  promotedObjectEventType: "PURCHASE",
});

const pad = (n: number) => String(n).padStart(2, "0");
const toLocalInput = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const todayAt = (hour: number) => {
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  return toLocalInput(d);
};

// Empty by default for a genuinely new broadcast — only an existing draft
// being resumed (parseInstagramContent in api.ts, hydrated over this) shows a
// name, rather than every fresh broadcast starting pre-filled with demo copy.
export const createInitialState = (): InstagramAdState => ({
  senderId: "",
  senderHandle: "",
  goal: "Sales",
  name: "",
  buyingType: "auction",
  specialCategory: "",
  adSets: [createAdSet()],
  creative: {
    templateId: null,
    format: "single",
    media: null,
    primaryText: "",
    headline: "",
    cta: "",
    destinationUrl: "",
  },
  budget: {
    budgetType: "Daily Budget",
    amount: 1000,
    startAt: todayAt(14),
    timezone: TIMEZONES[0],
    hasEnd: false,
    endAt: todayAt(16),
  },
});

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// "2026-09-21T14:00" → "Today, 2:00 PM" / "Tomorrow, 2:00 PM" / "12 Sep, 2:00 PM"
export const formatWhen = (value: string): string => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const h = d.getHours();
  const time = `${h % 12 === 0 ? 12 : h % 12}:${pad(d.getMinutes())} ${h >= 12 ? "PM" : "AM"}`;
  const now = new Date();
  const diff = Math.round((startOfDay(d) - startOfDay(now)) / 86400000);
  if (diff === 0) return `Today, ${time}`;
  if (diff === 1) return `Tomorrow, ${time}`;
  const year = d.getFullYear() === now.getFullYear() ? "" : `, ${d.getFullYear()}`;
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${year}, ${time}`;
};

export const rupees = (n: number) => `₹${n.toLocaleString("en-IN")}`;
